import React, { useEffect, useState, useMemo } from 'react';
import WebAPIUtils from 'utils/AccessorialRequestWebAPIUtils';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { Input, Label } from '@cargomatic/ui';
import { Info } from 'lucide-react';
import LaneRateAuditTable from './LaneRateAuditTable';

const PER_PAGE = 10;

const LaneRateAudits = ({ rate, type }) => {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [searchEvent, setSearchEvent] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    setLoading(true);
    getAudits(rate, searchEvent);
  }, [page, skip, rate, searchEvent]);

  const onEventChange = (event) => {
    setSearchEvent(event);
  };

  const debouncedChangeHandler = useMemo(
    () => debounce(onEventChange, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    };
  }, []);

  function handlePageClick(selectedPage) {
    setLoading(true);
    setPage(selectedPage);
    setSkip(((selectedPage || page) - 1) * PER_PAGE);
  }

  const getAudits = async (rate, searchEvent) => {
    if (rate && rate.id) {
      let filterString =
        type === 'laneRates'
          ? { lanerateId: rate.id }
          : { laneaccessorialrateId: rate.id };

      if (searchEvent && searchEvent.length > 0) {
        filterString.event = searchEvent;
      }

      filterString = new URLSearchParams(filterString).toString();

      const fetchAPI =
        type === 'laneRates'
          ? WebAPIUtils.getUserLaneRateAudits
          : WebAPIUtils.getLaneAccessorialRateAudits;

      const response = await fetchAPI(
        `per_page=${PER_PAGE}&skip=${skip}&${filterString}`,
      );

      if (response) {
        const { audits, count, page } = response;

        const mappedResponse = audits.map((item) => {
          return {
            ...item,
            user: item.user || 'CSV Upload',
            customer: rate.name,
            originCity: rate.originCity,
            originZip: rate.originZip,
            destinationCity: rate.destinationCity,
            destinationZip: rate.destinationZip,
          };
        });
        setAudits(mappedResponse);
        setPages(Math.ceil(count / PER_PAGE));
        setTotal(count);
        setPage(page);
      }
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label
          htmlFor="audit-search"
          className="text-sm font-medium flex items-center gap-1"
        >
          {titleize(t('SEARCH'))}
          <Info
            className="h-3 w-3 text-muted-foreground"
            aria-hidden="true"
            focusable="false"
          />
        </Label>
        <p className="text-xs text-muted-foreground mb-1">
          {t('SEARCH_EVENT_AUDITS')}
        </p>
        <Input
          id="audit-search"
          className="w-60"
          placeholder={t('SEARCH_BY_EVENT_NAME')}
          onChange={(e) => debouncedChangeHandler(e.target.value)}
          name="event"
          type="text"
        />
      </div>
      <LaneRateAuditTable
        items={audits}
        perPage={PER_PAGE}
        page={page}
        pages={pages}
        total={total}
        onChange={() => {}}
        onPageClick={handlePageClick}
        loading={loading}
      />
    </div>
  );
};

export default LaneRateAudits;
